/**
 * Varumärkesnamn (company alias) för elbolag.
 *
 * I sökresultat och kundvända vyer visar vi alltid varumärkesnamnet – aldrig det
 * juridiska bolagsnamnet med "AB", "Aktiebolag", "(publ)" osv. Undantag: om
 * varumärkesnamnet självt innehåller AB (t.ex. "ABK Elhandel") behålls det.
 */

/** Marknadssuffix från affiliatenätverken, t.ex. "Fortum SE". */
const MARKET_SUFFIX = /\s+(se|sv|sweden|sverige)$/i;

/** Juridiska suffix som aldrig ska visas i kundvända vyer. */
const LEGAL_SUFFIX =
  /[\s,]+(ab\s*\(publ\)|aktiebolag|ab|hb|kb|ekonomisk\s+förening|ek\.?\s*för\.?)$/i;

/**
 * Statustext som affiliatenätverken lägger till i programnamnet, t.ex.
 * "Enkla Elbolaget - Programmet Pausar 2026-11-24". Den är intern information
 * och ska aldrig synas för kunden.
 */
const PROGRAM_STATUS =
  /\s*[-–—|:(]\s*(programmet\s+)?(pausar|pausas|pausat|pausad|avslutas|avslutat|stängs|stängt|upphör)\b.*$/i;

const tidy = (value: string) =>
  value.replace(PROGRAM_STATUS, "").replace(/\s+/g, " ").trim();

/**
 * Normaliserar versaler: "FORTUM" → "Fortum", "cheap energy" → "Cheap Energy".
 * Varje ord (även efter bindestreck och apostrof) får stor första bokstav,
 * resten gemener. Rena symbolord ("&") lämnas orörda.
 */
export function titleCaseBrand(value: string): string {
  return value.replace(
    /[\p{L}\p{N}][\p{L}\p{N}.]*/gu,
    (word) =>
      word.charAt(0).toLocaleUpperCase("sv-SE") +
      word.slice(1).toLocaleLowerCase("sv-SE"),
  );
}

/** Tar bort juridiska suffix i slutet av namnet ("Kärnfull Future AB" → "Kärnfull Future"). */
export function stripLegalSuffix(name: string | null | undefined): string {
  let out = tidy(name ?? "");
  let prev = "";
  while (out && out !== prev) {
    prev = out;
    out = tidy(out.replace(/\s*\(publ\)$/i, "").replace(LEGAL_SUFFIX, ""));
  }
  return out;
}

/** True om namnet ser ut som ett juridiskt bolagsnamn (slutar på AB o.likn.). */
export const hasLegalSuffix = (name: string | null | undefined) =>
  LEGAL_SUFFIX.test(tidy(name ?? "")) || /\(publ\)$/i.test(tidy(name ?? ""));

/**
 * Väljer varumärkesnamnet. `alias` (annonsörens titel/varumärke) vinner när det
 * finns och inte är ett juridiskt namn; annars används programnamnet rensat från
 * juridiska suffix. Marknadssuffix ("SE") tas alltid bort.
 */
export function brandName(
  primary: string | null | undefined,
  alias?: string | null | undefined,
  fallback = "",
): string {
  const clean = (value: string | null | undefined) =>
    titleCaseBrand(tidy(tidy(value ?? "").replace(MARKET_SUFFIX, "")));
  const aliasName = clean(alias);
  const primaryName = clean(primary);

  if (aliasName && !hasLegalSuffix(aliasName)) return aliasName;
  if (primaryName && !hasLegalSuffix(primaryName)) return primaryName;

  const stripped = stripLegalSuffix(aliasName) || stripLegalSuffix(primaryName);
  return stripped || aliasName || primaryName || titleCaseBrand(tidy(fallback));
}
